import { createTheme } from '@mui/material/styles';
import { colors, space, radius, shadow, breakpoints, typography, transition, statusColors } from '../theme/tokens';

/**
 * Dizzaroo Web UI Addendum v0.1 – Main app theme.
 * Primary: Deep Blue #1E73BE. All UI text: Urbanist.
 */
const theme = createTheme({
  palette: {
    primary: {
      main: colors?.primary?.[500] ?? '#1E73BE',      // #1E73BE Deep Blue
      light: colors?.primary?.light ?? '#4A8FD4',
      dark: colors?.primary?.[600] ?? '#1E6091',      // #1E6091
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors?.accent?.teal ?? '#168AAD',
      light: colors?.accent?.tealLight ?? '#168AAD',
      dark: colors?.accent?.tealDark ?? '#168AAD',
      contrastText: '#ffffff',
    },
    success: {
      main: colors.success,
      contrastText: '#ffffff',
    },
    warning: {
      main: colors.warning,
      contrastText: '#ffffff',
    },
    error: {
      main: colors.error,
      contrastText: '#ffffff',
    },
    background: {
      default: colors.neutral[100],
      paper: '#ffffff',
    },
    text: {
      primary: colors.neutral[900],
      secondary: colors.neutral[700],
    },
    // Expose Dizzaroo tokens for use in sx/component overrides
    dizzaroo: {
      primary: colors.primary,
      accent: colors.accent,
      neutral: colors.neutral,
    },
  },
  typography: {
    fontFamily: typography.fontFamily,
    // Page title: 28–32px, 900
    h1: {
      fontFamily: typography.fontFamily,
      fontWeight: 900,
      fontSize: '1.75rem',   // 28px
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: typography.fontFamily,
      fontWeight: 700,
      fontSize: '1.375rem', // 22px
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: typography.fontFamily,
      fontWeight: 700,
      fontSize: '1.25rem',   // 20px
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: typography.fontFamily,
      fontWeight: 600,
      fontSize: '1.125rem', // 18px
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: typography.fontFamily,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: typography.fontFamily,
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontFamily: typography.fontFamily,
      fontWeight: 500,
      fontSize: '0.9375rem',
    },
    subtitle2: {
      fontFamily: typography.fontFamily,
      fontWeight: 500,
      fontSize: '0.8125rem',
    },
    body1: {
      fontFamily: typography.fontFamily,
      fontWeight: 400,
      fontSize: '0.875rem',  // 14px
    },
    body2: {
      fontFamily: typography.fontFamily,
      fontWeight: 400,
      fontSize: '0.8125rem',
    },
    button: {
      fontFamily: typography.fontFamily,
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    caption: {
      fontFamily: typography.fontFamily,
      fontWeight: 500,
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: radius.md,
  },
  spacing: 8, // MUI base unit; use theme.spacing(n) for space.1=4 → theme.spacing(0.5), etc.
  breakpoints: {
    ...breakpoints,
  },
  shadows: [
    shadow.none,
    shadow.sm,
    shadow.md,
    shadow.lg,
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    shadow.lg, shadow.lg, shadow.lg, shadow.lg, shadow.lg,
    shadow.lg, shadow.lg, shadow.lg, shadow.lg, shadow.lg,
    shadow.lg, shadow.lg, shadow.lg, shadow.lg, shadow.lg,
    shadow.lg, shadow.lg, shadow.lg, shadow.lg, shadow.lg,
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800;900&display=swap');
      `,
    },
    MuiButton: {
      defaultProps: {
        color: 'primary',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 40,
          borderRadius: radius.md,
          paddingLeft: space[4],
          paddingRight: space[4],
          fontWeight: 600,
          fontSize: '0.875rem',
          boxShadow: shadow.none,
          transition: `background-color ${transition.fast} ${transition.easing}, border-color ${transition.fast} ${transition.easing}, box-shadow ${transition.fast} ${transition.easing}, color ${transition.fast} ${transition.easing}`,
          '&:hover': {
            boxShadow: shadow.sm,
          },
          '&:active': {
            boxShadow: shadow.none,
          },
          // MUI applies `color="primary"` to every button by default (defaultProps
          // above), and the contained/outlined slots below set their own
          // background/border/color for '&.MuiButton-colorPrimary' with the SAME
          // CSS specificity (one class) as this rule — a same-specificity tie goes
          // to whichever rule is later in the generated stylesheet, which in
          // testing turned out to be the colorPrimary rule, so a disabled primary
          // button rendered in full color instead of looking disabled. Nesting the
          // disabled override inside each colorPrimary/colorSecondary block below
          // (two classes, ".MuiButton-colorPrimary.Mui-disabled") is the fix that
          // doesn't depend on stylesheet order or !important support — plain CSS
          // specificity (two classes beats one) guarantees it wins. This root-level
          // rule stays as the fallback for variants with no color-specific override
          // (e.g. the plain "outlined"/"text" base, or a future variant).
          '&.Mui-disabled': {
            backgroundColor: colors.neutral[100],
            color: colors.neutral[500],
            borderColor: colors.neutral[200],
          },
        },
        sizeSmall: {
          minHeight: 32,
          fontSize: '0.8125rem',
          paddingLeft: space[3],
          paddingRight: space[3],
        },
        sizeLarge: {
          minHeight: 48,
          fontSize: '0.9375rem',
          paddingLeft: space[5],
          paddingRight: space[5],
        },
        contained: {
          '&:hover': {
            boxShadow: shadow.sm,
          },
          '&.MuiButton-colorPrimary': {
            backgroundColor: colors.primary[500],
            color: '#ffffff',
            '&:hover': { backgroundColor: colors.primary[600] },
            '&.Mui-disabled': {
              backgroundColor: colors.neutral[100],
              color: colors.neutral[500],
            },
          },
          '&.MuiButton-colorSecondary': {
            backgroundColor: colors.accent.teal,
            color: '#ffffff',
            '&:hover': { backgroundColor: colors.accent.tealDark },
            '&.Mui-disabled': {
              backgroundColor: colors.neutral[100],
              color: colors.neutral[500],
            },
          },
        },
        outlined: {
          borderColor: colors.neutral[200],
          color: colors.neutral[900],
          '&.MuiButton-colorPrimary': {
            borderColor: colors.primary[500],
            color: colors.primary[500],
            '&:hover': {
              borderColor: colors.primary[600],
              backgroundColor: 'rgba(30, 115, 190, 0.06)',
            },
            '&.Mui-disabled': {
              borderColor: colors.neutral[200],
              color: colors.neutral[500],
            },
          },
          '&:hover': {
            borderColor: colors.neutral[500],
            backgroundColor: colors.neutral[50],
          },
        },
        text: {
          '&.MuiButton-colorPrimary': {
            color: colors.primary[500],
            '&:hover': { backgroundColor: 'rgba(30, 115, 190, 0.06)' },
            '&.Mui-disabled': {
              color: colors.neutral[500],
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.md,
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: colors.neutral[200],
        },
        elevation0: {
          boxShadow: 'none',
          border: `1px solid ${colors.neutral[200]}`,
        },
        elevation1: {
          boxShadow: shadow.sm,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${colors.neutral[200]}`,
          boxShadow: shadow.sm,
          transition: `box-shadow ${transition.base} ${transition.easing}, border-color ${transition.base} ${transition.easing}, transform ${transition.base} ${transition.easing}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: space[5],
          '&:last-child': { paddingBottom: space[5] },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        colorDefault: {
          backgroundColor: statusColors.default.bg,
          color: statusColors.default.color,
        },
        colorPrimary: {
          backgroundColor: statusColors.submitted.bg,
          color: statusColors.submitted.color,
        },
        colorSecondary: {
          backgroundColor: colors.neutral[100],
          color: colors.accent.teal,
        },
        colorSuccess: {
          backgroundColor: statusColors.approved.bg,
          color: statusColors.approved.color,
        },
        colorWarning: {
          backgroundColor: statusColors.draft.bg,
          color: statusColors.draft.color,
        },
        colorError: {
          backgroundColor: statusColors.rejected.bg,
          color: statusColors.rejected.color,
        },
        outlined: {
          borderColor: colors.neutral[200],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 2.5,
          borderRadius: radius.sm,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
          color: colors.neutral[700],
          '&.Mui-selected': {
            color: colors.primary[500],
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: colors.neutral[50],
            color: colors.neutral[700],
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: `1px solid ${colors.neutral[200]}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color ${transition.fast} ${transition.easing}`,
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:hover': {
            backgroundColor: colors.neutral[50],
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.neutral[200]}`,
          fontSize: '0.875rem',
          padding: `${space[3]}px ${space[4]}px`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.xl,
          boxShadow: shadow.xl,
          border: 'none',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 700,
          padding: `${space[5]}px ${space[5]}px ${space[2]}px`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `${space[3]}px ${space[5]}px`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${space[4]}px ${space[5]}px`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
          border: `1px solid ${colors.neutral[200]}`,
          marginTop: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          margin: `2px ${space[1]}px`,
          fontSize: '0.875rem',
          '&.Mui-selected': {
            backgroundColor: 'rgba(30, 115, 190, 0.1)',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: shadow.lg,
          border: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: `border-color ${transition.fast} ${transition.easing}, box-shadow ${transition.fast} ${transition.easing}`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.neutral[200],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[500],
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.neutral[900],
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: radius.sm,
          padding: `${space[1]}px ${space[2]}px`,
        },
        arrow: {
          color: colors.neutral[900],
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.neutral[200],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: `background-color ${transition.fast} ${transition.easing}`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          fontSize: '0.875rem',
        },
        standardSuccess: {
          backgroundColor: statusColors.approved.bg,
          color: statusColors.approved.color,
        },
        standardWarning: {
          backgroundColor: statusColors.draft.bg,
          color: statusColors.draft.color,
        },
        standardError: {
          backgroundColor: statusColors.rejected.bg,
          color: statusColors.rejected.color,
        },
        standardInfo: {
          backgroundColor: statusColors.submitted.bg,
          color: statusColors.submitted.color,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${colors.primary[500]}`,
            outlineOffset: 2,
          },
        },
      },
    },
  },
});

// Attach raw tokens for use in sx or custom components
theme.dizzarooTokens = { colors, space, radius, shadow, typography, transition, statusColors };

export default theme;
